import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  SelectGroup, SelectLabel,
} from '@/components/ui/select';
import { RefreshCw, Loader2, AlertTriangle, Power, ArrowUp, ArrowDown, ShieldAlert } from 'lucide-react';
import {
  forexService,
  fmtForexSymbol,
  contractToLots,
  lotsToContracts,
  instrumentClassLabel,
  type ForexService,
  type FxcmHealth,
  type FxcmSettings,
  type FxcmAccount,
  type FxcmPosition,
  type FxcmOrder,
  type FxcmInstrument,
  type FxcmQuote,
  type PlaceMarketOrder,
} from '@/services/forexService';

// Fallback for the order-entry picker before the live instrument list
// loads (or if the instruments endpoint fails) — a few majors so the
// dropdown is never empty. The real list comes from svc.getInstruments().
const FALLBACK_INSTRUMENTS: FxcmInstrument[] = [
  { symbol: 'EURUSD', displaySymbol: 'EUR/USD', instrumentClass: 'forex', contractMultiplier: 100_000, pointSize: 0.0001, isTradingAllowed: true },
  { symbol: 'GBPUSD', displaySymbol: 'GBP/USD', instrumentClass: 'forex', contractMultiplier: 100_000, pointSize: 0.0001, isTradingAllowed: true },
  { symbol: 'USDJPY', displaySymbol: 'USD/JPY', instrumentClass: 'forex', contractMultiplier: 1_000, pointSize: 0.01, isTradingAllowed: true },
];

function fmtMoney(n: number | null | undefined, frac = 2): string {
  if (n == null) return '-';
  return n.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac });
}

function fmtRate(n: number | null | undefined, symbol?: string): string {
  if (n == null) return '-';
  // JPY pairs quote to 3 decimals, everything else to 5. Standard forex convention.
  const isJpy = (symbol ?? '').toUpperCase().includes('JPY');
  return n.toLocaleString('en-US', {
    minimumFractionDigits: isJpy ? 3 : 5,
    maximumFractionDigits: isJpy ? 3 : 5,
  });
}

function pnlClass(n: number | null | undefined): string {
  if (n == null || n === 0) return '';
  return n > 0 ? 'text-green-500' : 'text-red-500';
}

/** Parse a user-entered numeric field strictly: the whole trimmed string
 *  must be a finite positive number. Returns null otherwise. Unlike
 *  parseFloat, Number() rejects partial input ("1abc", "1,0950" → NaN),
 *  so a malformed lot / stop / limit entry can't be silently truncated
 *  into a wrong value on these risk-bearing controls. */
function strictPositiveNumber(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * FXCM forex trading panel. Used in two places, distinguished by props:
 *   • Paper Trading → Forex tab — demo account (svc=forexService).
 *   • Live Trading page — funded real account (svc=forexLiveService,
 *     live=true): adds a red real-money banner and a mandatory order
 *     confirmation step before anything is submitted.
 */
export function ForexPanel({
  svc = forexService,
  live = false,
}: {
  svc?: ForexService;
  live?: boolean;
}) {
  const [health, setHealth] = useState<FxcmHealth | null>(null);
  const [settings, setSettings] = useState<FxcmSettings | null>(null);
  const [account, setAccount] = useState<FxcmAccount | null>(null);
  const [positions, setPositions] = useState<FxcmPosition[]>([]);
  const [orders, setOrders] = useState<FxcmOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tradeable instruments (forex + commodities + indices + crypto …).
  // Fetched once when the broker first becomes reachable; FXCM's
  // instrument set doesn't change intraday.
  const [instruments, setInstruments] = useState<FxcmInstrument[]>(FALLBACK_INSTRUMENTS);
  // One-shot guard for the instrument fetch. A ref (not a setState
  // updater side-effect) so it stays a pure render and StrictMode's
  // double-invoke can't fire two requests. Reset on failure to retry.
  const instrumentsFetched = React.useRef(false);

  // Live bid/ask per instrument — polled on a fast cadence (separate from
  // the 15s account/positions refresh) so the quote strip feels live.
  const [quotesBySymbol, setQuotesBySymbol] = useState<Map<string, FxcmQuote>>(new Map());

  // Quick-order form state
  const [orderSymbol, setOrderSymbol] = useState('EURUSD');
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [orderLots, setOrderLots] = useState('0.01');
  const [orderStop, setOrderStop] = useState('');   // optional stop-loss rate
  const [orderLimit, setOrderLimit] = useState(''); // optional take-profit rate
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<string | null>(null);
  // Live-trading only: a validated order parked awaiting explicit
  // confirmation. null = nothing pending. Demo submits straight through.
  const [confirmOrder, setConfirmOrder] = useState<PlaceMarketOrder | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      // Settings + health are cheap and tell us whether to bother with the rest.
      const [h, s] = await Promise.all([svc.health(), svc.getSettings()]);
      setHealth(h);
      setSettings(s);

      // Only call broker-touching endpoints if we have creds AND the broker is reachable.
      // Otherwise we'd just keep getting 404/500 every refresh.
      if (h.brokerReachable) {
        const [a, p, o] = await Promise.all([
          svc.getAccount().catch(() => null),
          svc.getPositions().catch(() => [] as FxcmPosition[]),
          svc.getRecentOrders(50).catch(() => [] as FxcmOrder[]),
        ]);
        setAccount(a);
        setPositions(p);
        setOrders(o);
        // Instruments are static intraday — fetch once. The ref guard
        // keeps this out of the setState updater (must stay pure) and
        // prevents a duplicate request under StrictMode.
        if (!instrumentsFetched.current) {
          instrumentsFetched.current = true;
          svc.getInstruments()
            .then((list) => { if (list.length > 0) setInstruments(list); })
            .catch(() => { instrumentsFetched.current = false; /* retry next refresh */ });
        }
      } else {
        setAccount(null);
        setPositions([]);
        setOrders([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [svc]);

  useEffect(() => {
    void loadAll();
    // 15s refresh — reads are cheap (no wire call; SDK in-memory tables)
    const id = setInterval(() => void loadAll(), 15_000);
    return () => clearInterval(id);
  }, [loadAll]);

  // Invalidate the instrument cache when the FXCM account changes — the
  // tradable set and contract multipliers are account-specific, so if the
  // user switches credentials while the panel stays mounted the picker
  // and instrumentBySymbol map would otherwise stay pinned to the old
  // account. Clearing the one-shot guard makes the next loadAll refetch.
  // (First mount: accountId goes null→id, a harmless extra reset.)
  const accountId = health?.accountId ?? null;
  useEffect(() => {
    instrumentsFetched.current = false;
    setInstruments(FALLBACK_INSTRUMENTS);
  }, [accountId]);

  // Fast quote poll (4s) — only while the broker is reachable. The
  // /quotes endpoint is an in-memory offers snapshot, cheap to hit.
  const brokerReachable = health?.brokerReachable ?? false;
  useEffect(() => {
    if (!brokerReachable) {
      setQuotesBySymbol(new Map());
      return;
    }
    let cancelled = false;
    const pull = async () => {
      try {
        const qs = await svc.getQuotes();
        if (!cancelled) setQuotesBySymbol(new Map(qs.map((q) => [q.symbol, q])));
      } catch { /* transient — keep last quotes */ }
    };
    void pull();
    const id = setInterval(() => void pull(), 4_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [brokerReachable, svc]);

  // symbol → instrument, for per-instrument contract sizing.
  const instrumentBySymbol = React.useMemo(() => {
    const m = new Map<string, FxcmInstrument>();
    for (const i of instruments) m.set(i.symbol, i);
    return m;
  }, [instruments]);

  // Instruments grouped by asset class for the order-entry picker.
  // Only tradable instruments are offered — FXCM also returns view-only /
  // disabled offers, and selecting one would just fail at the broker
  // boundary. instrumentBySymbol above keeps the FULL set so the
  // positions/orders tables can still size a symbol that has since gone
  // view-only. Class display order: forex first, then alphabetical.
  const groupedInstruments = React.useMemo(() => {
    const byClass = new Map<string, FxcmInstrument[]>();
    for (const i of instruments) {
      if (!i.isTradingAllowed) continue;
      const arr = byClass.get(i.instrumentClass) ?? [];
      arr.push(i);
      byClass.set(i.instrumentClass, arr);
    }
    return [...byClass.entries()]
      .map(([cls, items]) => ({
        cls,
        label: instrumentClassLabel(cls),
        items: items.slice().sort((a, b) => a.displaySymbol.localeCompare(b.displaySymbol)),
      }))
      .sort((a, b) =>
        a.cls === 'forex' ? -1 : b.cls === 'forex' ? 1 : a.label.localeCompare(b.label));
  }, [instruments]);

  // Display label for a symbol — prefer the instrument's authoritative
  // displaySymbol (e.g. "SPX500", "XAU/USD") over fmtForexSymbol, which
  // mangles non-forex 6-char names ("SPX500" → "SPX/500").
  const displaySym = useCallback((symbol: string): string =>
    instrumentBySymbol.get(symbol)?.displaySymbol ?? fmtForexSymbol(symbol),
  [instrumentBySymbol]);

  // Lots ↔ contract-units using the instrument's real multiplier; falls
  // back to the JPY-vs-other heuristic when the instrument isn't loaded.
  const lotsForSymbol = useCallback((symbol: string, amount: number): number => {
    const mult = instrumentBySymbol.get(symbol)?.contractMultiplier;
    return mult && mult > 0 ? amount / mult : contractToLots(symbol, amount);
  }, [instrumentBySymbol]);
  const unitsForSymbol = useCallback((symbol: string, lots: number): number => {
    const mult = instrumentBySymbol.get(symbol)?.contractMultiplier;
    return mult && mult > 0 ? Math.round(lots * mult) : lotsToContracts(symbol, lots);
  }, [instrumentBySymbol]);

  const onToggleEnabled = useCallback(async () => {
    if (!settings) return;
    try {
      const next = !settings.tradingEnabled;
      const updated = await svc.setEnabled(next);
      setSettings(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [settings, svc]);

  // Submit an already-validated order to the broker. Demo calls this
  // directly from onPlaceOrder; live routes through the confirm dialog.
  const submitOrder = useCallback(async (req: PlaceMarketOrder) => {
    setOrderResult(null);
    setOrderSubmitting(true);
    try {
      const r = await svc.placeMarketOrder(req);
      const prot = [
        req.stopPrice != null ? `SL ${req.stopPrice}` : null,
        req.limitPrice != null ? `TP ${req.limitPrice}` : null,
      ].filter(Boolean).join(', ');
      // SL/TP are echoed as "requested" — the protective orders are
      // submitted on the same valuemap but we don't read the trade row
      // back here to confirm FXCM actually attached them. Verify on the
      // positions table / FXCM until that read-back lands.
      setOrderResult(
        `Order ${r.orderId} submitted: ${req.side} ${req.lots} ${displaySym(req.symbol)}`
        + (prot ? ` (${prot} requested)` : ''));
      // Clear the protective-rate fields so they can't silently carry
      // over to the next order — stale SL/TP would likely be on the
      // wrong side after a side flip or symbol change.
      setOrderStop('');
      setOrderLimit('');
      void loadAll();
    } catch (e) {
      setOrderResult(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOrderSubmitting(false);
    }
  }, [svc, displaySym, loadAll]);

  const onPlaceOrder = useCallback(() => {
    setOrderResult(null);
    // Strict numeric parse: Number() (unlike parseFloat) rejects partial
    // input — "1abc", "1,0950" → NaN — so a malformed lot/SL/TP entry can
    // never be silently truncated into a wrong (and risk-bearing) value.
    const lots = strictPositiveNumber(orderLots);
    if (lots == null) {
      setOrderResult('Lots must be a positive number (e.g. 0.01 micro, 0.1 mini, 1.0 standard)');
      return;
    }
    // SL/TP are optional; an empty field omits that protection. A
    // non-empty field must parse to a positive rate. FXCM does the
    // authoritative correct-side validation on submit.
    let stopPrice: number | undefined;
    let limitPrice: number | undefined;
    if (orderStop.trim() !== '') {
      stopPrice = strictPositiveNumber(orderStop) ?? undefined;
      if (stopPrice == null) {
        setOrderResult('Stop-loss must be a positive rate, or left blank');
        return;
      }
    }
    if (orderLimit.trim() !== '') {
      limitPrice = strictPositiveNumber(orderLimit) ?? undefined;
      if (limitPrice == null) {
        setOrderResult('Take-profit must be a positive rate, or left blank');
        return;
      }
    }
    const req: PlaceMarketOrder = { symbol: orderSymbol, side: orderSide, lots, stopPrice, limitPrice };
    // Live trading is real money — never submit without an explicit
    // second click on the confirmation dialog. Demo submits straight away.
    if (live) {
      setConfirmOrder(req);
      return;
    }
    void submitOrder(req);
  }, [orderSymbol, orderSide, orderLots, orderStop, orderLimit, live, submitOrder]);

  const onClosePosition = useCallback(async (symbol: string) => {
    try {
      await svc.closePosition(symbol);
      void loadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [loadAll, svc]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading FXCM forex panel…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Live real-money banner ───────────────────────────────── */}
      {live && (
        <Card className="border-2 border-red-500 bg-red-500/10">
          <CardContent className="p-3 flex items-center gap-2 text-sm font-semibold text-red-500">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            LIVE TRADING — orders here place real trades with real money on your funded FXCM account.
          </CardContent>
        </Card>
      )}

      {/* ── Status banner ────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${health?.brokerReachable ? 'bg-green-500' : 'bg-red-500'}`}
              aria-hidden
            />
            <span className="text-sm font-medium">
              {health?.brokerReachable ? 'FXCM connected' : 'FXCM offline'}
            </span>
            {health?.accountId && (
              <span className="text-xs text-muted-foreground">· acct {health.accountId}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex-1 truncate">{health?.message}</div>
          <Button
            variant={settings?.tradingEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={onToggleEnabled}
            disabled={!settings}
          >
            <Power className="h-3 w-3 mr-1" />
            {settings?.tradingEnabled ? 'Trading ON' : 'Trading OFF'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void loadAll()}>
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-500/50">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" /> {error}
          </CardContent>
        </Card>
      )}

      {/* ── Account snapshot ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="Account" value={account?.accountId ?? '-'} />
          <Field label="Status" value={account?.status ?? '-'} />
          <Field label="Balance" value={fmtMoney(account?.balance)} />
          <Field label="Equity" value={fmtMoney(account?.equity)} />
          <Field label="Used margin" value={fmtMoney(account?.usedMargin)} />
          <Field label="Usable margin" value={fmtMoney(account?.usableMargin)} />
          <Field
            label="Margin level"
            value={account?.marginLevelPercent != null ? `${account.marginLevelPercent.toFixed(0)}%` : '—'}
          />
          <Field label="Locked" value={account?.isLocked ? 'YES' : 'no'} />
        </CardContent>
      </Card>

      {/* ── Quick order form ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open trade (market)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          {/* Live bid/ask strip for the selected instrument. */}
          <QuoteStrip
            quote={quotesBySymbol.get(orderSymbol) ?? null}
            instrument={instrumentBySymbol.get(orderSymbol) ?? null}
            displaySymbol={instrumentBySymbol.get(orderSymbol)?.displaySymbol
              ?? fmtForexSymbol(orderSymbol)}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Instrument</label>
            <Select value={orderSymbol} onValueChange={setOrderSymbol}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {groupedInstruments.map(g => (
                  <SelectGroup key={g.cls}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map(i => (
                      <SelectItem key={i.symbol} value={i.symbol}>{i.displaySymbol}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Side</label>
            <Select value={orderSide} onValueChange={(v) => setOrderSide(v as 'buy' | 'sell')}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buy">Buy</SelectItem>
                <SelectItem value="sell">Sell</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Lots</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderLots}
              onChange={(e) => setOrderLots(e.target.value)}
              className="w-24 px-2 py-1 text-sm rounded border bg-background"
              placeholder="0.01"
            />
            <span className="text-[10px] text-muted-foreground">
              ≈ {unitsForSymbol(orderSymbol, parseFloat(orderLots) || 0).toLocaleString()} units
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Stop-loss</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderStop}
              onChange={(e) => setOrderStop(e.target.value)}
              className="w-28 px-2 py-1 text-sm rounded border bg-background"
              placeholder="optional"
            />
            <span className="text-[10px] text-muted-foreground">attached stop rate</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Take-profit</label>
            <input
              type="text"
              inputMode="decimal"
              value={orderLimit}
              onChange={(e) => setOrderLimit(e.target.value)}
              className="w-28 px-2 py-1 text-sm rounded border bg-background"
              placeholder="optional"
            />
            <span className="text-[10px] text-muted-foreground">attached limit rate</span>
          </div>
          <Button
            onClick={onPlaceOrder}
            disabled={orderSubmitting || !settings?.tradingEnabled || confirmOrder != null}
            title={!settings?.tradingEnabled ? 'Enable trading first' : ''}
            className={live ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
          >
            {orderSubmitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            {orderSide === 'buy' ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
            {live ? 'Review order' : 'Submit'}
          </Button>
          {orderResult && (
            <div className="basis-full text-xs text-muted-foreground">{orderResult}</div>
          )}

          {/* Live-trading confirmation — a mandatory second click before
              any real-money order leaves the browser. */}
          {confirmOrder && (
            <div className="basis-full rounded-md border-2 border-red-500 bg-red-500/10 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-500">
                <ShieldAlert className="h-4 w-4" />
                Confirm LIVE order — this places a REAL order with REAL money
              </div>
              <div className="text-sm font-mono">
                {confirmOrder.side.toUpperCase()} {confirmOrder.lots} {displaySym(confirmOrder.symbol)}
                {confirmOrder.stopPrice != null && <> · SL {confirmOrder.stopPrice}</>}
                {confirmOrder.limitPrice != null && <> · TP {confirmOrder.limitPrice}</>}
                {' '}≈ {unitsForSymbol(confirmOrder.symbol, confirmOrder.lots).toLocaleString()} units
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={orderSubmitting}
                  onClick={() => {
                    const req = confirmOrder;
                    setConfirmOrder(null);
                    void submitOrder(req);
                  }}
                >
                  {orderSubmitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Confirm &amp; place real order
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={orderSubmitting}
                  onClick={() => setConfirmOrder(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Open positions ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open positions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Pair</th>
                <th className="p-2">Side</th>
                <th className="p-2 text-right">Lots</th>
                <th className="p-2 text-right">Open</th>
                <th className="p-2 text-right">Current</th>
                <th className="p-2 text-right">P&amp;L</th>
                <th className="p-2 text-right">P&amp;L %</th>
                <th className="p-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground text-xs">No open positions</td></tr>
              )}
              {positions.map(p => (
                <tr key={`${p.symbol}_${p.side}_${p.openRate}`} className="border-t">
                  <td className="p-2">{displaySym(p.symbol)}</td>
                  <td className="p-2">{p.side}</td>
                  <td className="p-2 text-right">{lotsForSymbol(p.symbol, p.amount).toFixed(2)}</td>
                  <td className="p-2 text-right">{fmtRate(p.openRate, p.symbol)}</td>
                  <td className="p-2 text-right">{fmtRate(p.currentRate, p.symbol)}</td>
                  <td className={`p-2 text-right ${pnlClass(p.unrealizedPnL)}`}>{fmtMoney(p.unrealizedPnL)}</td>
                  <td className={`p-2 text-right ${pnlClass(p.unrealizedPnLPercent)}`}>
                    {p.unrealizedPnLPercent != null ? `${p.unrealizedPnLPercent.toFixed(2)}%` : '-'}
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onClosePosition(p.symbol)}>Close</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Recent orders ────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent orders</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Time</th>
                <th className="p-2">Pair</th>
                <th className="p-2">Side</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-right">Rate</th>
                <th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground text-xs">No recent orders</td></tr>
              )}
              {orders.map(o => (
                <tr key={o.orderId} className="border-t">
                  <td className="p-2 text-xs">{o.submittedAt ? new Date(o.submittedAt).toLocaleString() : '-'}</td>
                  <td className="p-2">{displaySym(o.symbol)}</td>
                  <td className="p-2">{o.side}</td>
                  <td className="p-2 text-right">{lotsForSymbol(o.symbol, o.amount).toFixed(2)}</td>
                  <td className="p-2 text-right">{fmtRate(o.rate, o.symbol)}</td>
                  <td className="p-2"><span className="text-xs">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

/** Live bid/ask/spread strip for the currently-selected instrument.
 *  Spread is shown in points (raw spread / instrument point size) — the
 *  conventional forex "pips" figure — falling back to the raw rate diff
 *  when point size is unknown. Renders placeholders until the first
 *  quote for the instrument arrives. */
function QuoteStrip({
  quote, instrument, displaySymbol,
}: {
  quote: FxcmQuote | null;
  instrument: FxcmInstrument | null;
  displaySymbol: string;
}) {
  // Decimal places from the instrument's point size + 1 fractional digit
  // (the conventional "fractional pip" / sub-point precision): EUR/USD
  // pointSize 0.0001 → 5dp, USD/JPY 0.01 → 3dp, gold/indices scale too.
  // Falls back to the JPY-vs-other heuristic when point size is unknown
  // (instrument not yet loaded).
  const isJpy = displaySymbol.toUpperCase().includes('JPY');
  const dp = instrument && instrument.pointSize > 0
    ? Math.max(0, Math.round(-Math.log10(instrument.pointSize))) + 1
    : (isJpy ? 3 : 5);
  const spreadPts = quote && instrument && instrument.pointSize > 0
    ? quote.spread / instrument.pointSize
    : null;
  return (
    <div className="basis-full flex items-center gap-6 rounded border bg-muted/30 px-3 py-2 text-sm">
      <span className="font-medium">{displaySymbol}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Bid</span>
        <span className="font-mono text-red-500">
          {quote ? quote.bid.toFixed(dp) : '—'}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Ask</span>
        <span className="font-mono text-green-500">
          {quote ? quote.ask.toFixed(dp) : '—'}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-muted-foreground">Spread</span>
        <span className="font-mono">
          {spreadPts != null ? `${spreadPts.toFixed(1)} pts` : '—'}
        </span>
      </span>
      {!quote && (
        <span className="text-[10px] text-muted-foreground">waiting for first tick…</span>
      )}
    </div>
  );
}
